import { partitionFilters } from 'components/filter/filters-next/upstream';
import { FilterGroup } from 'components/filter/types';
import { omit } from 'lodash';
import { FilterCacheKey } from 'types/filter-cache-key';
import { OutboundFilters } from 'types/request';
import { createWithEqualityFn as create } from 'zustand/traditional';

interface FilterCacheData {
    filterGroups?: FilterGroup[];
    dirty?: boolean;
    segmentId?: number;
}

interface FiltersCache {
    filters: {
        contacts?: FilterCacheData;
        companies?: FilterCacheData;
        plans?: FilterCacheData;
        campaigns?: FilterCacheData;
        transactions?: FilterCacheData;
        engage?: FilterCacheData;
        tickets?: FilterCacheData;
        'relational-campaigns'?: FilterCacheData;
        pledges?: FilterCacheData;
        mailingsTracking?: FilterCacheData;
        dataHygieneAddresses?: FilterCacheData;
        dataHygieneEmails?: FilterCacheData;
        dataHygienePhoneNumbers?: FilterCacheData;
        tasks?: FilterCacheData;
        reports?: FilterCacheData;
        singleReport?: FilterCacheData;
        tableFilters?: FilterCacheData;
        tableFiltersBar?: FilterCacheData;
        accountMembers?: FilterCacheData;
        attendees?: FilterCacheData;
        auctionItems?: FilterCacheData;
        quickBooksTransactions?: FilterCacheData;
    };
    setFilters: (filters: FiltersCache['filters']) => void;
    setSegmentIdByKey: (type: FilterCacheKey, id?: number) => void;
    updateFiltersNextByKey: (
        type: FilterCacheKey,
        filters: FilterCacheData['filterGroups'],
    ) => void;
    clearFilterByKey: (type: FilterCacheKey) => void;
    setDirtyByKey: (type: FilterCacheKey, isDirty: boolean) => void;
    getRequestValuesByKey: (type: FilterCacheKey) => OutboundFilters;
}

const useFiltersCache = create<FiltersCache>((set, get) => ({
    filters: {},
    setFilters: filters => set({ filters }),
    setSegmentIdByKey: (type, segmentId) =>
        set(state => ({
            filters: {
                ...state.filters,
                [type]: {
                    ...(state.filters[type] ?? {}),
                    segmentId,
                },
            },
        })),
    updateFiltersNextByKey: (type, filters) =>
        set(state => ({
            filters: {
                ...state.filters,
                [type]: {
                    ...(state.filters[type] ?? {}),
                    filterGroups: filters,
                    dirty: true,
                },
            },
        })),
    clearFilterByKey: type =>
        set(state => ({
            filters: omit(state.filters, type),
        })),
    getRequestValuesByKey: type =>
        partitionFilters(get().filters[type]?.filterGroups ?? []) as OutboundFilters,
    setDirtyByKey: (type, dirty) =>
        set(state => ({
            filters: {
                ...state.filters,
                [type]: {
                    ...(state.filters[type] ?? {}),
                    dirty,
                },
            },
        })),
}));

export { useFiltersCache };
export type { FiltersCache, FilterCacheData };
